/**
 * ТЗ 0024 — со-финансирование. Domain model + label helpers.
 *
 * Every vocabulary here travels as a plain lowercase String on the wire
 * (`waiting_intake`, `bur`, `ambiguous`) — the backend deliberately avoids
 * graphene auto-enums, which would serialize them uppercase. Compare against
 * these constants, never against a hand-typed literal.
 */

export type ProgramType = 'bur' | 'kfs';

export const CANDIDATE_STATUSES = [
  'new',
  'investigating',
  'info_prepared',
  'agreed_to_try',
  'agreement_requested',
  'prereg_warned',
  'preregistered',
  'waiting_intake',
  'intake_notified',
  'waiting_decision',
  'predecision_positive',
  'decision_positive',
  'agreement_signed',
  'declined',
  'lost',
] as const;

export type CandidateStatus = typeof CANDIDATE_STATUSES[number];

export const TERMINAL_STATUSES: string[] = [
  'agreement_signed',
  'declined',
  'lost',
];

/** i18n keys, so both locales stay in one place. */
export const statusLabelKey = (status: string): string =>
  `pages.dofinansowanie.status.${status}`;

export const programLabel = (program: string): string =>
  program === 'kfs' ? 'KFS' : 'BUR';

export interface IFundingIntake {
  id: string;
  startsOn?: string | null;
  endsOn?: string | null;
  status: string;
  /** Фаза из дат — считает СЕРВЕР тем же выражением, что и фильтр списка.
   *  Клиент её больше не выводит: два определения разъехались бы. */
  phase?: string | null;
  /** Сколько дней осталось. Пусто — у бессрочного окна конца нет. */
  daysLeft?: number | null;
  /** Когда человек подтвердил набор. Заполнено — робот рассылает по этому
   *  окну; пусто — молчит. Единственное разрешение на рассылку (ТЗ 0054). */
  confirmedAt?: string | null;
  /** Кто подтвердил. Имя, а не id: «подтверждено» без «кем» через неделю не
   *  даёт сделать ничего. */
  confirmedByName?: string | null;
}

export interface IFundingOperatorRef {
  id: string;
  name: string;
  programType: string;
  /** С кем работает: `b2b` / `b2c`. Здесь, а не в `IFundingOperator`, — ссылка
      на оператора рисуется в воронке и в карточке кандидата, и B2B/B2C обязан
      быть виден и там (ТЗ 0053). */
  clientType?: string | null;
}

export interface IFundingOperator extends IFundingOperatorRef {
  programName?: string | null;
  /** Nr projektu (#96). С него начинается ID wsparcia участников; по нему же
   *  ищет реестр. Пустая строка — номер не внесён. */
  projectNumber?: string | null;
  isActive: boolean;
  coverageScope: string;
  voivodeshipName?: string | null;
  powiatNames: string[];
  /** Raw coverage strings the import could not map — a visible gap, not a
   * silent one. */
  powiatsUnresolved: string[];
  maxAmount?: number | null;
  maxAmountCurrency?: string | null;
  ownContributionPercent?: number | null;
  paymentMethod?: string | null;
  decisionDays?: number | null;
  rollingIntake: boolean;
  instructionUrl?: string | null;
  instructionReady: boolean;
  newsUrl?: string | null;
  /**
   * С кем работает оператор: `b2b` (заявку подаёт работодатель) или `b2c`.
   *
   * Считает СЕРВЕР из колонки Notion `Dla pracodawcy` — второй вид того же
   * признака разъехался бы с первым на первой правке (ТЗ 0052).
   */
  clientType?: string | null;
  /** Период набора: `running` / `soon` / `rolling` / `none`. */
  intakePeriod?: string | null;
  /** В названии стоит FIRMY, а галка «для работодателя» — нет. Значение
      `clientType` этим НЕ переопределяется, но данные стоит поправить. */
  nameSaysB2b?: boolean | null;
  /** Что робот видит на странице: `changed` / `quiet` / `never`. Три
      значения и один вопрос — «обновилась или нет» (ТЗ 0054). Считает
      сервер: фильтр, счётчик и подпись обязаны быть одним выражением. */
  pageState?: string | null;
  /** Есть ли подтверждённое живое окно, то есть пишет ли робот кандидатам.
      Ось НЕЗАВИСИМАЯ от `intakePeriod`: «идёт» ещё не значит «рассылаем». */
  hasConfirmedIntake?: boolean | null;
  /** Ссылок в подписи. Ноль — робот слеп: страница открылась, а
      фингерпринтить нечего (ТЗ 0049). */
  pageLinksCount?: number | null;
  /** Адрес слежения выглядит не страницей новостей: search / analytics /
      epoch / homepage. Пусто — вопросов нет (ТЗ 0049). */
  urlSuspect?: string | null;
  pageChanged: boolean;
  pageChangedAt?: string | null;
  /** Когда робот последний раз ходил на страницу. Пусто — не ходил ни разу. */
  pageHashCheckedAt?: string | null;
  candidatesCount: number;
  currentIntake?: IFundingIntake | null;
  /** Все окна оператора. Реестру они нужны не для показа, а чтобы назвать в
   *  колонке ближайшее окно и сказать, подтверждено оно или нет. */
  intakes?: IFundingIntake[] | null;
  comment?: string | null;
  internalNote?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface IFundingCandidateRow {
  id: string;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  status: string;
  employmentStatus?: string | null;
  city?: string | null;
  isDeposit: boolean;
  matchStatus: string;
  isStudent: boolean;
  studentId?: string | null;
  linkedUserName?: string | null;
  voivodeshipName?: string | null;
  powiatName?: string | null;
  powiatRaw?: string | null;
  /** Пометка «ведущий». Никого не глушит (ТЗ 0067) и часто пуста. */
  primaryOperator?: IFundingOperatorRef | null;
  /** АКТИВНЫЕ операторы: письма о наборах идут от каждого. */
  operators: IFundingOperatorRef[];
  activeOperatorsCount: number;
  disabledOperatorsCount: number;
  /** Полный журнал связей — только в карточке, список его не запрашивает. */
  operatorLinks?: IFundingOperatorLink[];
}

/**
 * Причины, по которым оператора выключают у кандидата.
 *
 * Зеркало `dofinansowanie/constants.py::LINK_OFF_REASONS`. Подпись сервер
 * присылает рядом (`deactivationReasonLabel`), но список нужен и здесь —
 * его выбирают в диалоге выключения.
 */
export const OPERATOR_OFF_REASONS = [
  'operator_declined',
  'person_declined',
  'transferred',
  'other',
] as const;

export type TOperatorOffReason = typeof OPERATOR_OFF_REASONS[number];

export const operatorOffReasonKey = (reason: string): string =>
  `pages.dofinansowanie.operatorOff.reason.${reason}`;

/** Одна связь «кандидат ↔ оператор»: активная или выключенная с причиной. */
export interface IFundingOperatorLink {
  id: string;
  isActive: boolean;
  isPrimary: boolean;
  source: string;
  addedAt?: string | null;
  addedByName?: string | null;
  deactivatedAt?: string | null;
  deactivatedByName?: string | null;
  deactivationReason?: string | null;
  deactivationReasonLabel?: string | null;
  note?: string | null;
  operator: IFundingOperator;
}

/**
 * Активные и выключенные — ОДНОЙ функцией на весь домен.
 *
 * Посчитанное дважды разъезжается: в этом домене так уже случилось со
 * счётчиком «подошло: N», который считали не тем предикатом, что список.
 */
export const splitOperatorLinks = (links?: IFundingOperatorLink[] | null) => ({
  active: (links || []).filter((link) => link.isActive),
  past: (links || []).filter((link) => !link.isActive),
});

export interface IBoardGroup {
  key: string;
  title: string;
  statuses: string[];
  count: number;
}

export interface IFundingBoard {
  total: number;
  ambiguousMatches: number;
  unresolvedPlaces: number;
  depositWaiting: number;
  /** Сколько операторов ждут, чтобы человек посмотрел изменившуюся страницу. */
  pageChangedOperators: number;
  suspiciousUrls: number;
  /**
   * Кандидаты без оператора — вся очередь подбора целиком.
   *
   * 🔴 Здесь была пара `operatorNone` / `operatorSeveral`. Оба считались по
   * M2M «кого рассматривали», который заполняют импорт из Notion и ручное
   * назначение, а не правила подбора: на 200 свежих кандидатах два числа не
   * совпали ни разу, и «не подошёл никто» по правилам не получается вовсе.
   */
  operatorMissing: number;
  /** Заявки, разобранные, но без назначенного оператора. */
  submissionsNeedOperator: number;
  groups: IBoardGroup[];
  /** Второй вид доски (ТЗ 0058): те же статусы в 4 категориях владельца. */
  stages: IBoardGroup[];
  buckets: { status: string; count: number }[];
}

export interface IGeoAlias {
  id: string;
  rawValue: string;
  status: string;
  hits: number;
  powiatName?: string | null;
  /** Вид привязанного повята: земский или город на правах повята. Здесь он
   *  дороже, чем где-либо ещё, — это экран, на котором сырую строку и
   *  привязывают. */
  powiatKind?: string | null;
  suggestedPowiatId?: string | null;
  suggestedPowiatName?: string | null;
  suggestedPowiatKind?: string | null;
  suggestionScore?: number | null;
  /** Воеводство, в контексте которого строку увидели, — вторая половина
   *  ключа и то, чем сужается справочник при разборе. */
  contextVoivodeshipId?: string | null;
  contextVoivodeshipName?: string | null;
  /** Для «неоднозначно» — между чем выбирать. Пусто у остальных статусов. */
  candidatePowiats: {
    id: string;
    name: string;
    kind?: string | null;
    voivodeshipName?: string | null;
  }[];
}

/** Формы занятости — зеркало `dofinansowanie/constants.py`. */
export const EMPLOYMENT_STATUSES = [
  'civil_contract',
  'employment_contract',
  'self_employed',
  'unemployed_registered',
  'unemployed_unregistered',
  'student',
] as const;

/** Наличие работодателя — единственное, что делает KFS применимым вообще. */
export const EMPLOYED_STATUSES: string[] = [
  'civil_contract',
  'employment_contract',
  'self_employed',
];

export const CANDIDATE_OUTCOMES = [
  'approved',
  'declined',
  'withdrawn',
  'expired',
] as const;

export const PREFERRED_TRACKS = [
  'devops',
  'python',
  'data_science',
  'cybersecurity',
] as const;

export const YES_NO_NA = ['yes', 'no', 'not_applicable'] as const;

export const employmentLabelKey = (value: string): string =>
  `pages.dofinansowanie.employment.${value}`;

export const outcomeLabelKey = (value: string): string =>
  `pages.dofinansowanie.outcome.${value}`;

export const trackLabelKey = (value: string): string =>
  `pages.dofinansowanie.tracks.${value}`;

export type TriState = 'yes' | 'no' | 'unknown';

/**
 * Трёхпозиционный ответ анкеты.
 *
 * Все булевы поля анкеты на бэкенде `null=True`, и это не небрежность: в
 * источнике «нет» означало «нет ИЛИ не спрашивали». Чекбокс молча превратил
 * бы «не знаем» в «нет» и отсёк бы людей — ровно та ловушка, из-за которой
 * при переходе порога 55→50 пришлось обнулить 771 ответ.
 */
export const triState = (value?: boolean | null): TriState => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'unknown';
};

export const fromTriState = (value: TriState): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};

/**
 * Письмо, которое робот отправил кандидату (ТЗ 0045).
 *
 * `kind` — повод: `operator_info` | `intake_soon` | `intake_running`.
 * `status` — `pending` | `sent` | `failed`.
 */
export interface IFundingNotification {
  id: string;
  kind: string;
  status: string;
  email?: string | null;
  reason?: string | null;
  sentAt?: string | null;
  createdAt: string;
  operatorId?: string | null;
  operatorName?: string | null;
  /** С кем работает оператор письма: `b2b` / `b2c` (ТЗ 0053). */
  operatorClientType?: string | null;
  intakeId?: string | null;
  intakeStartsOn?: string | null;
  intakeEndsOn?: string | null;
  /** ТЗ 0085: шаблон SendGrid, с которым ушло письмо, и его имя снимком. */
  templateId?: string | null;
  templateName?: string | null;
  /** Правило письма; пусто — правило удалено. */
  ruleId?: string | null;
  /** Срок правила СЕЙЧАС: «за N дней» / «раз в N дней». */
  ruleOffsetDays?: number | null;
  ruleEveryDays?: number | null;
  /** Переменные шаблона (JSONString): у отправленного — как ушли. */
  dynamicData?: unknown;
  /** Ключ повода: второй раз по нему письмо не уйдёт. */
  dedupKey?: string | null;
  sgMessageId?: string | null;
  /** Прогон робота, отправивший письмо. */
  runId?: string | null;
  /** Кому — в ОБЩЕМ журнале строка обязана сама это говорить. */
  candidateId: string;
  candidateName?: string | null;
  /** Итог доставки одним словом; «отправлено» значит лишь «SendGrid принял». */
  deliveryState: string;
  deliveredAt?: string | null;
  firstOpenedAt?: string | null;
  openCount: number;
  bounceReason?: string | null;
  dropReason?: string | null;
}

/** Заявка с публичной формы: `state` = failed | pending | linked | rejected. */
export interface IFormSubmission {
  id: string;
  email?: string | null;
  phone?: string | null;
  submittedAt: string;
  processedAt?: string | null;
  processError?: string | null;
  candidateId?: string | null;
  candidateName?: string | null;
  utmSource?: string | null;
  utmCampaign?: string | null;
  /** honeypot | too_fast — заявку сочли автоматической и не разбирали. */
  rejectedReason?: string | null;
  /**
   * failed | pending | needs_operator | linked | rejected.
   *
   * 🔴 `linked` разделено надвое (ТЗ 0050): «разобрана» означала лишь
   * «строка кандидата создана» и молчала о том, движется ли человек. На
   * живых данных ВСЕ пять «разобранных» заявок стояли без оператора.
   */
  state: string;
  /* Контекст ручного разбора прямо в строке: выбор оператора зависит от
     географии (BUR — повят прописки, KFS — работодателя) и формы занятости,
     и без этих фактов он был бы вслепую. */
  candidateStatus?: string | null;
  candidateOperatorId?: string | null;
  /** ID всех активных операторов — по ним сверяется состав в разборе. */
  candidateOperatorIds?: string[];
  candidateOperatorName?: string | null;
  candidateVoivodeship?: string | null;
  candidateVoivodeshipId?: string | null;
  candidateEmploymentStatus?: string | null;
  candidateEmployerName?: string | null;
  /**
   * Гео-идентификаторы, которых нет в нашем справочнике (ТЗ 0061).
   *
   * Пусто, пока сайт шлёт наши идентификаторы. Непустое означает ровно одно:
   * человек уехал в ручной разбор не потому, что не заполнил географию, а
   * потому что форма прислала не наш повят. Сервер такое значение
   * отбрасывает молча — чтобы форма не стала оракулом, — и без этого поля
   * разница на экране не видна вовсе.
   */
  geoRejected?: { key: string; value: string }[] | null;
  /* Тяжёлое: приходит только одиночным запросом для окна разбора (ТЗ 0051). */
  /** Что именно прислала форма — известные поля в устойчивом порядке. */
  payloadAnswers?: { field: string; value: string }[] | null;
  /**
   * Откуда у кандидата оператор: `robot` / `manual` / `import` / пусто.
   *
   * 🔴 Прямой ответ на вопрос владельца «что такое разобрано автоматически».
   * На 27.08.2026 это `import` у ВСЕХ 673 кандидатов с оператором: автоподбор
   * не отработал ни разу, журнал воронки пуст.
   */
  candidateOperatorSource?: string | null;
  candidateOperatorSetBy?: string | null;
  candidateOperatorSetAt?: string | null;
}

/** Прогон фоновой задачи домена — одна таблица журналит и импорт, и робота. */
export interface IFundingTaskRun {
  id: string;
  taskName: string;
  /** `beat` | `cli` | `erp`. Без этого ночной прогон робота и ручная
   *  перепроверка в журнале неотличимы. */
  triggerSource?: string | null;
  /** Кто запустил. Пусто у расписания и у запусков из терминала. */
  startedByName?: string | null;
  /** Сухой прогон: посчитано всё, не записано ничего. */
  dryRun?: boolean | null;
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
  stats?: unknown;
  /** ТЗ 0087: когда снята выгрузка источника, которую разложил прогон. */
  sourceExportedAt?: string | null;
}

/** Строка сводки: одна задача робота и её последний прогон. */
export interface IFundingTaskSummary {
  task: string;
  taskName: string;
  /** Ходит ли по расписанию. Все три флага по умолчанию выключены. */
  scheduled: boolean;
  scheduleFlag: string;
  lastRun?: IFundingTaskRun | null;
}

/** Прогон, который запустил человек, а не расписание. */
export const runIsManual = (run: IFundingTaskRun): boolean =>
  run.triggerSource === 'cli' || run.triggerSource === 'erp';

/**
 * Полное имя задачи → короткое. Зеркало `AUTOMATION_TASKS` на бэкенде.
 *
 * 🔴 Подпись задачи ОДНА на кнопку запуска и на строку журнала. Два словаря
 * («Обход страниц» у кнопки, «Обход страниц операторов» в строке) разъехались
 * бы на первой же правке.
 *
 * И ключ собирается по КОРОТКОМУ имени: в полном есть точки, а i18next
 * считает точку разделителем пути — такой ключ находится только через
 * запасной механизм, и проверка паритета локалей его не видит.
 */
export const TASK_SHORT_NAMES: Record<string, string> = {
  // ТЗ 0087: первой строкой, как в `AUTOMATION_TASKS` на бэкенде.
  'dofinansowanie.notion_sync': 'notion',
  'dofinansowanie.watch_operator_pages': 'watch',
  'dofinansowanie.match_new_candidates': 'match',
  'dofinansowanie.notify_upcoming_intakes': 'notify-soon',
  'dofinansowanie.notify_running_intakes': 'notify-running',
  'dofinansowanie.close_finished_intakes': 'close',
  'dofinansowanie.import': 'import',
};

/** ТЗ 0087: синхронизация из Notion — короткое и полное имя задачи. */
export const NOTION_SYNC_TASK = 'notion';
export const NOTION_SYNC_TASK_NAME = 'dofinansowanie.notion_sync';

/** Ключ подписи задачи; `null` — имя незнакомое, покажем его как есть. */
export const runTaskKey = (taskName: string): string | null => {
  const short = TASK_SHORT_NAMES[taskName];
  return short ? `pages.dofinansowanie.runs.short.${short}` : null;
};

/**
 * Зеркало общего union `TagTone` из `_detailShared/detail.styled`. Держим
 * копию здесь, чтобы `core/**` не импортировал вверх, в `components/**`, и не
 * ломал направление импортов Atomic Design — тот же приём, что в
 * `core/hooks/CareerCenter/careerModel.ts`.
 */
export type TagTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

/* ──────────────────────────────────────────────────── тона для плашек ──
 *
 * 🔴 Один словарь на раздел, а не `switch` по компонентам. Раньше тон
 * считался (`statusTone`), но до экрана не доезжал: `data-tone` вешали на
 * `S.NativeSelect`, у которого нет ни одного правила `[data-tone]`, а чипы
 * в журнале писем и заявках рисовались вовсе без тона — «доставлено»,
 * «отскок» и «ошибка» выглядели одинаково серыми.
 *
 * Имена тонов — из `L.Tag` (`_detailShared/detail.styled.ts`), а не свои:
 * плашка и словарь обязаны говорить на одном языке.
 */

/**
 * Статус кандидата. Терминально-отрицательные — единственные, что обязаны
 * читаться как беда: `agreement_signed` тоже терминальный, но это цель.
 */
export const candidateStatusTone = (status: string): TagTone => {
  if (status === 'agreement_signed' || status === 'decision_positive') {
    return 'ok';
  }
  if (status === 'declined' || status === 'lost') return 'danger';
  if (status === 'waiting_intake' || status === 'waiting_decision') {
    return 'warn';
  }
  return 'neutral';
};

export const candidateMatchTone = (matchStatus: string): TagTone => {
  if (matchStatus === 'auto' || matchStatus === 'manual') return 'ok';
  if (matchStatus === 'ambiguous') return 'warn';
  return 'neutral';
};

/**
 * 🔴 Программа — это личность, а не подпись.
 *
 * BUR и KFS различались ОДНИМ серым чипом с текстом, и владелец сказал прямо:
 * «бурки, ФС — друг от друга невозможно». Тон живёт здесь, значок — в
 * `components/pages/Dofinansowanie/programIdentity.tsx` (в `core/**` нет и не
 * должно быть импортов из `lucide-react`, это слой выше).
 *
 * Синий против жёлтого, а не два оттенка серого: различие обязано читаться
 * боковым зрением в таблице на 50 строк.
 */
export const programTone = (program: string): TagTone =>
  program === 'kfs' ? 'gold' : 'info';

/** Доставка письма. `pending` — жёлтый: это «ещё в пути», а не отказ. */
export const deliveryTone = (state: string): TagTone => {
  switch (state) {
    case 'opened':
      return 'ok';
    case 'delivered':
      return 'info';
    case 'pending':
      return 'warn';
    case 'bounced':
    case 'dropped':
    case 'failed':
      return 'danger';
    case 'unsubscribed':
      return 'gold';
    default:
      return 'neutral';
  }
};

export const deliveryLabelKey = (state: string): string =>
  `pages.dofinansowanie.mail.delivery.${state}`;

/**
 * Заявка с публичной формы. `rejected` — НЕ ошибка: так помечены отсечённые
 * ловушкой, и красный на них означал бы поломку, которой нет.
 */
export const submissionTone = (state: string): TagTone => {
  switch (state) {
    case 'linked':
      return 'ok';
    case 'pending':
      return 'warn';
    case 'failed':
      return 'danger';
    case 'rejected':
      return 'neutral';
    default:
      return 'neutral';
  }
};

/**
 * Фаза окна набора. Значения совпадают с `constants.INTAKE_PHASES` на
 * сервере — он их и считает.
 *
 * ⚠️ `ended`, а не `closed`: это ФАЗА из дат, а `closed` — хранимый статус,
 * который двигает ночная задача. Разные слова намеренно: спутав их, легко
 * снова начать фильтровать по одному, а показывать другое.
 */
export type IntakePhase =
  | 'upcoming'
  | 'running'
  | 'ended'
  | 'cancelled'
  | 'unknown';

export interface IIntakePhaseState {
  phase: IntakePhase;
  tone: TagTone;
  /** Ключ i18n подписи. */
  key: string;
  /** Дней до открытия (`upcoming`) или до закрытия (`running`); `null`, если
   *  посчитать не из чего — у окна без конца «сколько осталось» не бывает. */
  days: number | null;
}

/**
 * Подпись и тон фазы окна. САМУ фазу считает СЕРВЕР (спека 0055).
 *
 * 🔴 Здесь стояло вычисление из дат — сорок строк с разбором `status`,
 * `startsOn`, `endsOn` и подсчётом дней. Пока фазу только рисовали, это было
 * безобидно. Стоило завести фильтр «показать идущие» — и двух определений
 * стало нельзя: фильтр отбирал бы по серверному правилу, а колонка писала бы
 * по клиентскому, и они разошлись бы на первой же правке. Ровно так в этом
 * домене уже случилось с правилом «живо ли окно», написанным шестью
 * способами.
 *
 * Осталось то, за что отвечает клиент: слово и цвет.
 */
const PHASE_TONES: Record<string, TagTone> = {
  running: 'ok',
  upcoming: 'info',
  ended: 'neutral',
  cancelled: 'neutral',
  unknown: 'neutral',
};

export const intakePhase = (intake: {
  phase?: string | null;
  daysLeft?: number | null;
}): IIntakePhaseState => {
  const phase = (intake.phase || 'unknown') as IntakePhase;
  return {
    phase,
    tone: PHASE_TONES[phase] || 'neutral',
    key: `pages.dofinansowanie.intakePhaseName.${phase}`,
    days: intake.daysLeft ?? null,
  };
};

/**
 * Что робот видит на странице — ОДИН вопрос и три ответа (ТЗ 0054).
 *
 * Владелец: «просто знать, что страница изменений есть или изменений нет,
 * когда проверено, и всё. Никаких ошибок в интерфейсе видеть не хочу».
 *
 * 🔴 Здесь стояли ТРИ функции — `robotState` (цепочка из девяти значений),
 * `pageState` (шесть) и `mailingState` (пять). Все три пересказывали одно
 * булево поле `pageChanged` плюс состояние разрешения на рассылку, которое
 * переехало на само окно набора. Осталась одна.
 *
 * Само состояние считает СЕРВЕР — иначе счётчик сегмента отвечал бы про
 * загруженную страницу (50 из 127), а список про всю базу
 * (`counter-must-match-the-button`). Здесь только подпись и тон.
 */
export interface IRobotState {
  /** Готовый ключ i18n: подпись живёт в локали, а не в компоненте. */
  key: string;
  tone: TagTone;
}

const PAGE_TONES: Record<string, TagTone> = {
  changed: 'warn',
  // ⚠️ `never` — НЕ ошибка и не тревога: это отсутствие ответа. Красный тон
  // здесь означал бы «сломалось», а сломаться могло и ничего — оператора
  // могли завести вчера. Кто чинит робота, смотрит журнал прогонов.
  never: 'neutral',
  quiet: 'ok',
};

export const pageState = (operator: {
  pageState?: string | null;
}): IRobotState => {
  const state = operator.pageState || 'quiet';
  return {
    key: `pages.dofinansowanie.pageState.${state}`,
    tone: PAGE_TONES[state] || 'neutral',
  };
};

/**
 * 🔴 Разбор `stats` прогона — вместо вываливания сырого лога в ячейку.
 *
 * Владелец: «тут куча логов, непонятно, что это значит», «эти логи не должны
 * быть прямо сюда вписаны». И был прав буквально: в колонку «Результат»
 * печаталось `Object.entries(stats).join(' · ')`, а `notes` — это массив
 * строк вида `candidate 22ca90b4…: 2 operators, primary left empty for
 * review`, по строке НА КАЖДОГО кандидата. На экране это сотни внутренних
 * хешей подряд.
 *
 * Здесь `stats` раскладывается на две части: числа, которые можно подписать,
 * и `notes`, из которых извлекается ОДНО утверждение — сколько кандидатов
 * ждут ручного выбора оператора. Само утверждение превращается в работу:
 * рядом с ним стоит переход в воронку с фильтром `needsOperatorReview`.
 */
export interface IRunSummary {
  /** Подписываемые счётчики: `[ключ, значение]`. */
  counters: { key: string; value: number | string }[];
  /** Сколько кандидатов импортёр оставил без основного оператора. */
  needsOperatorReview: number;
  /** Полный текст заметок — под раскрытием, а не первым планом. */
  notes: string[];
}

const REVIEW_NOTE = /primary left empty for review/i;

/**
 * `stats` приезжает `JSONString`. Разбор ОДИН на всех потребителей: строку
 * журнала, карточку прогона и фразу-итог. Три копии `JSON.parse` с разными
 * защитами разъехались бы на первом же непарсящемся значении.
 */
export const runStats = (stats: unknown): Record<string, unknown> | null => {
  if (!stats) return null;
  let parsed: unknown = stats;
  if (typeof stats === 'string') {
    try {
      parsed = JSON.parse(stats);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  return parsed as Record<string, unknown>;
};

/** Ключи `stats` со строковым КОДОМ причины — не счётчики. */
const SERVICE_CODE_KEYS = new Set(['skipped', 'refused']);

export const runSummary = (stats: unknown): IRunSummary => {
  const empty: IRunSummary = {
    counters: [],
    needsOperatorReview: 0,
    notes: [],
  };
  if (!stats) return empty;

  const parsed = runStats(stats);
  // Не разобралось — показываем как есть: молча проглотить единственное, что
  // прогон о себе сказал, хуже, чем показать сырую строку.
  if (!parsed) {
    return typeof stats === 'string' ? { ...empty, notes: [stats] } : empty;
  }

  const counters: IRunSummary['counters'] = [];
  let notes: string[] = [];

  Object.entries(parsed).forEach(([key, value]) => {
    if (key === 'notes') {
      // `notes` приходит либо массивом, либо уже склеенной строкой —
      // зависит от того, чем задача его записала.
      notes = Array.isArray(value)
        ? value.map(String)
        : String(value ?? '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
      return;
    }
    // Вложенные объекты (`tables`, `match_counts`) в плашку не помещаются и
    // печатались бы как `[object Object]` — их разбор это отдельный экран,
    // а не строка журнала.
    // ТЗ 0087: `skipped` / `refused` — КОДЫ причины, а не числа. Чипом
    // «skipped: busy» они печатали бы служебное слово; их переводит экран.
    if (SERVICE_CODE_KEYS.has(key)) return;
    if (typeof value === 'number' || typeof value === 'string') {
      counters.push({ key, value });
    }
  });

  return {
    counters,
    needsOperatorReview: notes.filter((note) => REVIEW_NOTE.test(note)).length,
    notes,
  };
};

/**
 * 🔴 Что прогон сделал — ОДНОЙ фразой, а не столбиком безымянных чисел.
 *
 * Владелец про прежний вид: «непонятно, где что находится… ничего не
 * понимаю». Он был прав арифметически: в колонке шириной 190 px стояло до
 * двенадцати чипов одинакового веса, включая служебные («записей в журнале»),
 * и какое из чисел главное, экран не сообщал.
 *
 * Фраза собирается из частей: первая — что задача делала, остальные — чем
 * это кончилось. Нулевые части опускаются, поэтому спокойный прогон говорит
 * одну строку, а не перечисляет пять нулей. Полный список чисел никуда не
 * делся — он в карточке прогона.
 *
 * Комбинаторного i18n не нужно: каждая часть — свой ключ со своим числом.
 */
export interface IRunOutcomePart {
  /** Готовый ключ i18n. */
  key: string;
  /** Число для подстановки; `undefined` — часть без числа. */
  n?: number;
  /** Часть, которая зовёт человека: пропуски, ошибки, разбор. */
  attention?: boolean;
}

/** Целое из `stats`; всё нечисловое — ноль, чтобы не печатать `NaN`. */
const num = (source: Record<string, unknown>, key: string): number => {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/** Сумма поля по всем таблицам импорта (`stats.tables`). */
const sumTables = (source: Record<string, unknown>, key: string): number => {
  const { tables } = source;
  if (!tables || typeof tables !== 'object') return 0;
  return Object.values(tables as Record<string, unknown>).reduce<number>(
    (total, table) =>
      total +
      (table && typeof table === 'object' ? num(table as never, key) : 0),
    0,
  );
};

const part = (
  key: string,
  n: number,
  attention = false,
): IRunOutcomePart | null =>
  n > 0
    ? { key: `pages.dofinansowanie.runs.outcome.${key}`, n, attention }
    : null;

/**
 * ТЗ 0087 — отчёт синхронизации из Notion, разобранный ОДИН раз на всех
 * потребителей: плитки панели, строку журнала, фразу в шапке и текст
 * подтверждения.
 *
 * 🔴 «Notion главнее — в том, что изменили в Notion»: поле берётся из Notion,
 * только если его колонка там поменялась с прошлой выгрузки. Если то же поле
 * меняли и в ERP, правка ERP заменяется — и молча это нельзя, поэтому
 * `overwritten` здесь полноправное число, а не строка в заметках.
 */
export interface INotionSyncIds {
  createdCandidates: string[];
  createdOperators: string[];
  overwrittenCandidates: string[];
  overwrittenOperators: string[];
  adoptedCandidates: string[];
  statusChangedCandidates: string[];
  /** Статус в Notion не распознан и не применён — кому поправить ярлык. */
  statusUnknownCandidates: string[];
  missingCandidates: string[];
  missingOperators: string[];
}

export interface INotionSyncReport {
  dryRun: boolean;
  /** Прогон пропущен (не ошибка) — ни в Notion, ни в базу он не ходил. */
  skipped: boolean;
  /** Код пропуска: `busy` | `robot_mail_on`; `null` — не пропущен. */
  skipReason: string | null;
  /** Код отказа у упавшего прогона (`no_notion_key`, …); `null` — нет. */
  refused: string | null;
  /** Есть ли в `stats` отчёт импорта (у упавшего до выгрузки — нет). */
  hasReport: boolean;
  candidatesCreated: number;
  candidatesUpdated: number;
  operatorsCreated: number;
  operatorsUpdated: number;
  statusChanges: number;
  /** Статус из Notion не распознан — и потому НЕ применён. */
  statusUnknown: number;
  /** Строки, чья ручная правка ERP заменена (в сухом — заменится). */
  overwritten: number;
  /** Сколько ПОЛЕЙ заменено в этих строках. */
  overwrittenFields: number;
  /** Кандидаты ERP, склеенные со страницей Notion по e-mail или телефону. */
  adopted: number;
  linksAdded: number;
  linksDisabled: number;
  linksRevived: number;
  intakesCreated: number;
  intakesUpdated: number;
  intakesMoved: number;
  /**
   * Сколько строк ERP нет в Notion. `null` — не считалось: сервер считает
   * это ТОЛЬКО по полной выгрузке, обрубок пагинации неотличим от удаления.
   */
  missing: number | null;
  missingCandidates: number | null;
  missingOperators: number | null;
  conflicts: number;
  errors: number;
  geoUnresolved: number;
  ids: INotionSyncIds;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** Список id из отчёта — строками: так их ждут `?candidate=` и `?operator=`. */
const idList = (source: Record<string, unknown>, key: string): string[] => {
  const value = source[key];
  return Array.isArray(value)
    ? value
        .filter((item) => typeof item === 'number' || typeof item === 'string')
        .map(String)
    : [];
};

/** Строковый код из `stats` (`skipped`, `refused`); `true` — «без кода». */
const codeOf = (value: unknown): string | null => {
  if (typeof value === 'string' && value) return value;
  return value === true ? '' : null;
};

const sumNumbers = (values: unknown[]): number | null => {
  const numbers = values.filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value),
  );
  return numbers.length
    ? numbers.reduce((total, value) => total + value, 0)
    : null;
};

export const notionReport = (stats: unknown): INotionSyncReport => {
  const s = runStats(stats) ?? {};
  const tables = asRecord(s.tables);
  const candidates = asRecord(tables.candidates);
  const bur = asRecord(tables.operators_bur);
  const kfs = asRecord(tables.operators_kfs);
  const missingRaw = asRecord(s.missing_in_notion);
  const ids = asRecord(s.ids);
  const skipReason = codeOf(s.skipped);
  const refused = codeOf(s.refused);

  return {
    dryRun: s.dry_run === true,
    skipped: skipReason !== null,
    skipReason,
    refused: refused || null,
    hasReport: Object.keys(tables).length > 0,
    candidatesCreated: num(candidates, 'created'),
    candidatesUpdated: num(candidates, 'updated'),
    operatorsCreated: num(bur, 'created') + num(kfs, 'created'),
    operatorsUpdated: num(bur, 'updated') + num(kfs, 'updated'),
    statusChanges: num(s, 'status_changes'),
    statusUnknown: num(s, 'status_unknown'),
    overwritten: num(s, 'overwritten_erp_edits'),
    overwrittenFields: num(s, 'overwritten_fields'),
    adopted: num(s, 'adopted'),
    linksAdded: num(s, 'links_added'),
    linksDisabled: num(s, 'links_disabled'),
    linksRevived: num(s, 'links_revived'),
    intakesCreated: num(s, 'intakes_created'),
    intakesUpdated: num(s, 'intakes_updated'),
    intakesMoved: num(s, 'intakes_moved'),
    missing: sumNumbers(Object.values(missingRaw)),
    missingCandidates: sumNumbers([missingRaw.candidates]),
    // `operators` — новый ключ; `operators_bur`/`operators_kfs` — прежний.
    missingOperators: sumNumbers(
      Object.entries(missingRaw)
        .filter(([key]) => key.startsWith('operators'))
        .map(([, value]) => value),
    ),
    conflicts: sumTables(s, 'conflicts'),
    errors: sumTables(s, 'errors'),
    geoUnresolved: num(s, 'geo_unresolved_count'),
    ids: {
      createdCandidates: idList(ids, 'created_candidates'),
      createdOperators: idList(ids, 'created_operators'),
      overwrittenCandidates: idList(ids, 'overwritten_candidates'),
      overwrittenOperators: idList(ids, 'overwritten_operators'),
      adoptedCandidates: idList(ids, 'adopted_candidates'),
      statusChangedCandidates: idList(ids, 'status_changed_candidates'),
      statusUnknownCandidates: idList(ids, 'status_unknown_candidates'),
      missingCandidates: idList(ids, 'missing_candidates'),
      missingOperators: idList(ids, 'missing_operators'),
    },
  };
};

/**
 * 🔴 Причины отказа и пропуска — КОДАМИ с сервера, подписи — здесь.
 *
 * Текст сервера русский и для логов: польский менеджер видел бы его как
 * есть. Ключи — литералами, а не `${code}`: так их находит проверка
 * паритета локалей.
 */
const NOTION_REFUSAL_KEYS: Record<string, string> = {
  already_running: 'pages.dofinansowanie.notionSync.refusal.already_running',
  no_notion_key: 'pages.dofinansowanie.notionSync.refusal.no_notion_key',
  live_notion_blocked:
    'pages.dofinansowanie.notionSync.refusal.live_notion_blocked',
  robot_mail_on: 'pages.dofinansowanie.notionSync.refusal.robot_mail_on',
  notion_failed: 'pages.dofinansowanie.notionSync.refusal.notion_failed',
};

const NOTION_SKIP_KEYS: Record<string, string> = {
  busy: 'pages.dofinansowanie.notionSync.skip.busy',
  robot_mail_on: 'pages.dofinansowanie.notionSync.skip.robot_mail_on',
};

/** Пропуск строкой журнала — целой фразой («Пропущен: …»). */
const NOTION_SKIP_OUTCOME_KEYS: Record<string, string> = {
  busy: 'pages.dofinansowanie.runs.outcome.notionBusy',
  robot_mail_on: 'pages.dofinansowanie.runs.outcome.notionSkippedRobot',
};

/** Ключ подписи отказа; `null` — код незнакомый, показываем текст сервера. */
export const notionRefusalKey = (code?: string | null): string | null =>
  (code && NOTION_REFUSAL_KEYS[code]) || null;

/** Ключ причины пропуска — всегда есть: незнакомый код → «без причины». */
export const notionSkipKey = (code?: string | null): string =>
  (code && NOTION_SKIP_KEYS[code]) ||
  'pages.dofinansowanie.notionSync.skip.other';

/**
 * Статус прогона ДЛЯ ЭКРАНА: пропущенная синхронизация — не «успешно».
 *
 * Сервер пишет пропуск статусом `success` (это не ошибка), но плашка
 * «УСПЕШНО» у прогона, который не ходил в Notion, врёт о свежести данных.
 */
export const runDisplayStatus = (run: IFundingTaskRun): string =>
  run.taskName === NOTION_SYNC_TASK_NAME &&
  run.status === 'success' &&
  notionReport(run.stats).skipped
    ? 'skipped'
    : run.status;

/**
 * 🔴 Что изменила синхронизация — ОДНИМ списком на шапку и журнал.
 *
 * «Без изменений» говорится, только если ноль ВЕЗДЕ. Раньше считались лишь
 * новые и изменённые кандидаты, и прогон, который выключил шесть связей и
 * перенёс окно набора, назывался «без изменений».
 */
export const notionChangeParts = (
  report: INotionSyncReport,
): IRunOutcomePart[] =>
  [
    part('notionCreated', report.candidatesCreated),
    part('notionUpdated', report.candidatesUpdated),
    part('notionStatus', report.statusChanges),
    part('notionAdopted', report.adopted),
    part('notionOperators', report.operatorsCreated),
    part('notionOperatorsUpdated', report.operatorsUpdated),
    part(
      'notionLinks',
      report.linksAdded + report.linksDisabled + report.linksRevived,
    ),
    part(
      'notionIntakes',
      report.intakesCreated + report.intakesUpdated + report.intakesMoved,
    ),
    part('notionMissing', report.missing ?? 0, true),
  ].filter(Boolean) as IRunOutcomePart[];

/**
 * То, что ждёт человека: чьи ручные правки ERP заменены (в сухом прогоне —
 * заменятся) и у кого статус Notion не распознан. Одна функция на журнал и
 * на шапку раздела — иначе шапка молчала бы о главном.
 */
export const notionAttentionParts = (
  report: INotionSyncReport,
): IRunOutcomePart[] =>
  [
    part(
      report.dryRun ? 'notionWouldOverwrite' : 'notionOverwritten',
      report.overwritten,
      true,
    ),
    part('notionStatusUnknown', report.statusUnknown, true),
  ].filter(Boolean) as IRunOutcomePart[];

/**
 * Предпросмотр для подтверждения записи: последний СУХОЙ прогон, который
 * новее последней настоящей синхронизации. Сухой, снятый ДО записи, врёт —
 * те правки уже заменены, и называть их числом значит обещать не то.
 */
export const notionPreviewRun = (
  runs: (IFundingTaskRun | null | undefined)[],
  lastSuccess?: IFundingTaskRun | null,
): IFundingTaskRun | null => {
  const after = lastSuccess ? Date.parse(lastSuccess.startedAt) : null;
  const usable = (run: IFundingTaskRun) => {
    const report = notionReport(run.stats);
    return (
      run.dryRun === true &&
      run.status === 'success' &&
      report.hasReport &&
      !report.skipped &&
      (after === null || Date.parse(run.startedAt) > after)
    );
  };
  return (
    runs.filter((run): run is IFundingTaskRun => Boolean(run)).find(usable) ??
    null
  );
};

export const runOutcome = (
  taskName: string,
  stats: unknown,
  status?: string,
): IRunOutcomePart[] => {
  const s = runStats(stats);
  if (!s) return [];

  // 🔴 Булево, а не число — и потому раньше молча выбрасывалось.
  //
  // Это исход «задача вышла на первой строке и не сделала НИЧЕГО»: почта
  // домена выключена, а двигать воронку без письма нельзя. На экране он
  // выглядел пустым «—», то есть неотличимо от «прогон прошёл спокойно».
  if (s.skipped_mail_disabled === true) {
    return [
      {
        key: 'pages.dofinansowanie.runs.outcome.idleMailOff',
        attention: true,
      },
    ];
  }

  const short = TASK_SHORT_NAMES[taskName];
  const parts: (IRunOutcomePart | null)[] = [];

  if (short === 'watch') {
    parts.push(part('watched', num(s, 'total')));
    parts.push(part('changed', num(s, 'changed'), true));
    parts.push(part('unreachable', num(s, 'error'), true));
    parts.push(part('baseline', num(s, 'baseline')));
  } else if (short === 'match') {
    parts.push(part('leads', num(s, 'total')));
    parts.push(part('matched', num(s, 'matched')));
    parts.push(part('toInvestigation', num(s, 'to_investigation'), true));
  } else if (short === 'notify-soon' || short === 'notify-running') {
    parts.push(part('intakes', num(s, 'intakes')));
    parts.push(part('mailSent', num(s, 'mail_sent')));
    parts.push(part('mailWould', num(s, 'mail_preview')));
    parts.push(part('mailDuplicate', num(s, 'mail_duplicate')));
    parts.push(part('noAudience', num(s, 'no_audience'), true));
    parts.push(
      part(
        'skippedIntakes',
        num(s, 'not_approved_intake') + num(s, 'approval_expired'),
        true,
      ),
    );
  } else if (short === 'close') {
    parts.push(part('opened', num(s, 'opened')));
    parts.push(part('closed', num(s, 'closed')));
    parts.push(part('approvalsExpired', num(s, 'approvals_expired'), true));
  } else if (short === 'notion') {
    const report = notionReport(s);
    // 🔴 Пропущенный прогон — не «делать было нечего»: он не ходил в Notion
    // вовсе. Спутать эти два значит соврать про свежесть данных.
    if (report.skipped) {
      return [
        {
          key:
            (report.skipReason &&
              NOTION_SKIP_OUTCOME_KEYS[report.skipReason]) ||
            'pages.dofinansowanie.runs.outcome.notionSkipped',
        },
      ];
    }
    // Отказ до выгрузки: причина КОДОМ, и она — весь итог прогона.
    const refusalKey = notionRefusalKey(report.refused);
    if (refusalKey && !report.hasReport) {
      return [{ key: refusalKey, attention: true }];
    }
    parts.push(...notionChangeParts(report));
    // Главное число синхронизации — чьи ручные правки ERP ушли. В сухом
    // прогоне это ещё не случилось, и фраза другая.
    parts.push(...notionAttentionParts(report));
    parts.push(part('conflicts', report.conflicts, true));
    parts.push(part('importErrors', report.errors, true));
    parts.push(part('geoUnresolved', report.geoUnresolved, true));
  } else if (short === 'import') {
    // Все числа импорта лежат ВНУТРИ `stats.tables`, а плоский разбор
    // вложенные объекты выбрасывает — из тридцати чисел на экран попадали два.
    parts.push(
      part('imported', sumTables(s, 'created') + sumTables(s, 'updated')),
    );
    parts.push(part('unchanged', sumTables(s, 'unchanged')));
    parts.push(part('conflicts', sumTables(s, 'conflicts'), true));
    parts.push(part('importErrors', sumTables(s, 'errors'), true));
    parts.push(part('geoUnresolved', num(s, 'geo_unresolved_count'), true));
  }

  // Задача упала до того, как что-то посчитала, — но упасть она могла и в
  // середине, поэтому счётчики выше остаются.
  parts.push(part('crashed', num(s, 'crashed'), true));

  const filled = parts.filter(Boolean) as IRunOutcomePart[];
  if (filled.length) return filled;

  // 🔴 Ни одного ненулевого числа — но «ничего не сделал» и «не успел» это
  // разные новости, и различает их СТАТУС.
  //
  // Прогон, оборванный на середине, чисел не сохраняет: `_finish` до него не
  // дошёл. Сказать про него «отработал, делать было нечего» значит выдать
  // обрыв за спокойную ночь — ровно та ложь, которую этот экран и лечит.
  if (status === 'running') {
    return [
      {
        key: 'pages.dofinansowanie.runs.outcome.stillRunning',
        attention: true,
      },
    ];
  }
  if (status === 'failed') {
    return [
      {
        key: 'pages.dofinansowanie.runs.outcome.crashedEarly',
        attention: true,
      },
    ];
  }
  // Синхронизация, у которой ноль ВЕЗДЕ, — «без изменений»: та же фраза,
  // что в шапке раздела.
  if (short === 'notion') {
    return [{ key: 'pages.dofinansowanie.runs.outcome.notionNoChanges' }];
  }
  // Задача отработала, и делать ей было нечего — так и говорим, вместо
  // прочерка.
  return [{ key: 'pages.dofinansowanie.runs.outcome.nothingToDo' }];
};

/**
 * 🔴 Почему разбор заявки упал — словами, а не текстом исключения.
 *
 * Владелец: «не очень понятно, что происходит в заявках, где разбор упал».
 * В строке печаталось сырое сообщение сервера, например
 * `['Wartość „W ciągu 1–2 miesięcy" ma nieprawidłowy format daty…']` — это
 * ответ валидатора модели, а не объяснение человеку.
 *
 * Возвращаем ключ подсказки для частых причин; полный текст никуда не
 * девается — он остаётся под раскрытием, потому что чинить приходится по
 * нему.
 */
export const submissionFailureHint = (error?: string | null): string | null => {
  if (!error) return null;
  const text = error.toLowerCase();
  if (text.includes('format daty') || text.includes('date')) {
    return 'pages.dofinansowanie.submissions.failure.date';
  }
  if (text.includes('powiat') || text.includes('повят')) {
    return 'pages.dofinansowanie.submissions.failure.powiat';
  }
  if (text.includes('email') || text.includes('e-mail')) {
    return 'pages.dofinansowanie.submissions.failure.email';
  }
  if (text.includes('duplicate') || text.includes('unique')) {
    return 'pages.dofinansowanie.submissions.failure.duplicate';
  }
  return 'pages.dofinansowanie.submissions.failure.unknown';
};

/** Прогон фоновой задачи. */
export const runTone = (status: string): TagTone => {
  switch (status) {
    case 'success':
      return 'ok';
    case 'running':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
};

/** Окно набора: объявлено → идёт → закрыто. */
export const intakeTone = (status: string): TagTone => {
  switch (status) {
    case 'open':
      return 'ok';
    case 'announced':
      return 'info';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
};
